
import { getFormItemByID, showModalDialog } from "@/helpers/dialog";
import { readInputAsNumber } from "@/helpers/form";
import {
  getUSIEngineInfo,
  sendUSISetOption,
  showSelectFileDialog,
} from "@/ipc/renderer";
import {
  getUSIEngineOptionCurrentValue,
  mergeUSIEngineSetting,
  USIEngineSetting,
} from "@/settings/usi";
import { Mutation, useStore } from "@/store";
import { computed, defineComponent, onMounted, PropType, ref, Ref } from "vue";

export default defineComponent({
  name: "USIEngineOptionDialog",
  props: {
    latestEngineSetting: {
      type: Object as PropType<USIEngineSetting>,
      required: true,
    },
  },
  emits: ["ok", "cancel"],
  setup(props, context) {
    const dialog: Ref = ref(null);
    const engineNameInput: Ref = ref(null);
    const store = useStore();
    const latest = props.latestEngineSetting as USIEngineSetting;
    let engine = ref(latest);

    const options = computed(() => {
      return Object.values(engine.value.options)
        .sort((a, b): number => {
          const aIsUSI = a.name.startsWith("USI_");
          const bIsUSI = b.name.startsWith("USI_");
          if (aIsUSI !== bIsUSI) {
            return aIsUSI ? -1 : 1;
          }
          return a.name < b.name ? -1 : 1;
        })
        .map((option, index) => {
          return {
            ...option,
            inputId: "usiEngineOptionDialogOption" + index,
            value: getUSIEngineOptionCurrentValue(option),
          };
        });
    });

    store.commit(Mutation.RETAIN_BUSSY_STATE);
    onMounted(async () => {
      showModalDialog(dialog.value);
      try {
        engine.value = await getUSIEngineInfo(latest.path);
        mergeUSIEngineSetting(engine.value, latest);
        engineNameInput.value.value = engine.value.name;
        for (const option of options.value) {
          const elem = getFormItemByID(option.inputId);
          if (elem && option.value !== undefined) {
            elem.value = option.value + "";
          }
        }
      } catch (e) {
        store.commit(Mutation.PUSH_ERROR, e);
      } finally {
        store.commit(Mutation.RELEASE_BUSSY_STATE);
      }
    });

    const selectFile = async (id: string) => {
      store.commit(Mutation.RETAIN_BUSSY_STATE);
      try {
        const path = await showSelectFileDialog();
        const elem = getFormItemByID(id);
        if (path && elem) {
          elem.value = path;
        }
      } catch (e) {
        store.commit(Mutation.PUSH_ERROR, e);
      } finally {
        store.commit(Mutation.RELEASE_BUSSY_STATE);
      }
    };

    const sendOption = async (name: string) => {
      store.commit(Mutation.RETAIN_BUSSY_STATE);
      try {
        await sendUSISetOption(engine.value.path, name);
      } catch (e) {
        store.commit(Mutation.PUSH_ERROR, e);
      } finally {
        store.commit(Mutation.RELEASE_BUSSY_STATE);
      }
    };

    const reset = () => {
      engineNameInput.value.value = engine.value.defaultName;
      for (const option of options.value) {
        const elem = getFormItemByID(option.inputId);
        if (elem) {
          if (engine.value.options[option.name].default !== undefined) {
            elem.value = engine.value.options[option.name].default + "";
          } else {
            elem.value = "";
          }
        }
      }
    };

    const ok = () => {
      engine.value.name = engineNameInput.value.value;
      for (const option of options.value) {
        const elem = getFormItemByID(option.inputId);
        if (elem) {
          engine.value.options[option.name].value = !elem.value
            ? undefined
            : option.type === "spin"
            ? readInputAsNumber(elem as HTMLInputElement)
            : elem.value;
        }
      }
      context.emit("ok", engine.value);
    };

    const cancel = () => {
      context.emit("cancel");
    };

    return {
      engine,
      dialog,
      engineNameInput,
      options,
      selectFile,
      sendOption,
      reset,
      ok,
      cancel,
    };
  },
});
